/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { Badge, Button, Col, Form, InputGroup, Row, Stack } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs';
import { HiOutlineAdjustmentsHorizontal } from 'react-icons/hi2';
import { useDataContext } from '../../../context';
import { useNavbar } from '../../../context/DashNavbarContext';
import { useMyLocationContext } from '../../../context/LocationContext';
import { GetQueryParams } from '../../../types/api.type';
import { SavedLocationSelection } from '../../../services/helper';
import { titleCase } from '../../../utils/stringConversions';
import './CourseSearchBar.css';

interface CourseOmniSearchProps {
  onSearch?: (filter: string) => void;
  noButton?: boolean;
  initialValue?: string;
}

export const CourseOmniSearch: React.FC<CourseOmniSearchProps> = ({
                                                                    onSearch,
                                                                    noButton,
                                                                    initialValue
                                                                  }) => {
  const [globalFilter, setGlobalFilter] = useState<string>(initialValue ?? '');

  return (
      <Form.Group controlId="search">
        <InputGroup size="sm">
          <Form.Control
              type="text"
              placeholder="Search by keyword"
              aria-label="Search"
              name="search"
              defaultValue={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
              }}
          />
          {!noButton && (
              <Button
                  variant="primary"
                  onClick={() => onSearch?.(globalFilter)}
              >
                <BsSearch />
                &nbsp;SEARCH
              </Button>
          )}
        </InputGroup>
        <Form.Text>Keywords can include School/Venue, Session or Theme names</Form.Text>
      </Form.Group>
  );
};

interface CourseSearchBarProps {
  filters?: GetQueryParams;
  setFilters?: React.Dispatch<React.SetStateAction<GetQueryParams>>;
  filtersOnly?: boolean;
}

const originMatchesPreferredLocation = (
  originValue: string,
  preferredLocation: SavedLocationSelection | null
) => {
  if (!preferredLocation?.location) return false;
  const userOrigin = `${preferredLocation.location.lat}_${preferredLocation.location.lng}`;
  return originValue === userOrigin;
};

const CourseSearchBar: React.FC<CourseSearchBarProps> = ({ filters, setFilters, filtersOnly = false }) => {
  const { activeNavbar, setActiveNavbar, setFilterNavContent, setFilterNavOverlap } = useNavbar();
  const { myLocation: preferredLocation } = useMyLocationContext();
  const { getLocationTitle } = useDataContext();

  const applySearchFilter = (search: string) => {
    if (search === '') return;
    setFilters?.((old) => {
      let copy = JSON.parse(JSON.stringify(old)) as GetQueryParams;
      let filterIndex = copy?.data?.filterBy?.findIndex((f) => f.field === 'search') ?? -1;
      let newFilter = { field: 'search', operator: 'contains', value: search };
      if (copy.data) {
        if (copy.data.filterBy) {
          if (filterIndex !== -1) {
            copy.data.filterBy[filterIndex] = newFilter;
          } else {
            copy.data.filterBy.push(newFilter);
          }
        } else {
          copy.data.filterBy = [newFilter];
        }
      } else {
        copy.data = { filterBy: [newFilter] };
      }
      return copy;
    });
  };

  const clearFilter = (index: number, subIndex?: number) => {
    setFilters?.((old) => {
      let copy = JSON.parse(JSON.stringify(old)) as GetQueryParams;
      let filter = copy?.data?.filterBy?.[index];
      if (filter) {
        if (subIndex !== undefined) {
          let value = filter.value;
          let newValue: string | any[] = '';
          if (Array.isArray(value)) {
            value.splice(subIndex, 1);
            filter.value = value;
            newValue = value as any[];
          } else if (typeof value === 'string') {
            let values = value.split(',');
            values.splice(subIndex, 1);
            newValue = values.join(',');
            filter.value = newValue;
          }

          if (newValue?.length < 1) {
            copy.data?.filterBy?.splice(index, 1);
          } else {
            copy.data?.filterBy?.splice(index, 1, filter);
          }
        } else {
          copy.data?.filterBy?.splice(index, 1);
        }
        return copy;
      }
      return old;
    });
  };

  const FilterClearButton: React.FC<{ label: string; onClick: () => void }> = ({
                                                                                 label,
                                                                                 onClick
                                                                               }) => {
    return (
        <Badge
            className="filter-badge blueberry me-1"
            bg=""
            onClick={onClick}
        >
          {label} X
        </Badge>
    );
  };

  const filterButtons: Array<any> = [];
  filters?.data?.filterBy?.forEach((f, index) => {
    if (f.field === 'search') return; // search has its own input, skip it here
    let value = f.value as any;
    let content;
    // for multi-select filters (locations)
    if (Array.isArray(value)) {
      content = value.forEach((v: { label: string; value: number }, idx) => {
        filterButtons.push(
            <FilterClearButton
                key={`${index}-${idx}`}
                label={v.label}
                onClick={() => clearFilter(index, idx)}
            />
        );
      });
    }
    // for quick filters (course_type)
    else if (typeof value === 'string' && value.indexOf(',') !== -1) {
      content = (value as string).split(',').forEach((v: string, idx) => {
        filterButtons.push(
            <FilterClearButton
                key={`${index}-${idx}`}
                label={titleCase(v)}
                onClick={() => clearFilter(index, idx)}
            />
        );
      });
    }
    // everything else
    else {
      let label = titleCase(String(f.value));
      // distance filter
      if (['distance'].includes(f.field)) {
        label = `~${label} Miles`;
      }
      // origin filter — only show LocationSetter label when it matches the active origin
      if (['origin'].includes(f.field)) {
        const originValue = String(f.value);
        label = originMatchesPreferredLocation(originValue, preferredLocation)
            ? preferredLocation!.label
            : `Near (${originValue.split('_').join(', ')})`;
      }
      // location filter
      if (['location_id'].includes(f.field)) {
        label = getLocationTitle(String(f.value));
      }
      filterButtons.push(
          <FilterClearButton
              key={index}
              label={label}
              onClick={() => clearFilter(index)}
          />
      );
    }
  });

  if (filtersOnly) {
    return (
        <div hidden={!filters?.data?.filterBy?.filter(f => f.field !== 'search').length}>
          <div className="d-flex flex-wrap align-items-center justify-content-end">
            <span className="text-uppercase me-2">Applied Filters:</span>
            <div className="d-flex flex-wrap justify-content-end">{filterButtons}</div>
          </div>
        </div>
    );
  }

  return (
      <div className="course-searchbar">
        <Form>
          <Row className="align-items-start g-2">
            <Col xs={12}>
              <InputGroup>
                <Form.Control
                    type="text"
                    placeholder="Search by keyword"
                    aria-label="Search"
                    name="search"
                    defaultValue={filters?.data?.filterBy?.find((f) => f.field === 'search')?.value?.toString() ?? ''}
                    onChange={(e) => {
                      applySearchFilter(e.target.value);
                    }}
                />
                <Button
                    variant="primary"
                    onClick={() => applySearchFilter(
                        (document.querySelector('.course-searchbar input[name="search"]') as HTMLInputElement)?.value ?? ''
                    )}
                >
                  <BsSearch />&nbsp;SEARCH
                </Button>
              </InputGroup>
              <Form.Text>Keywords can include School/Venue, Session or Theme names</Form.Text>
            </Col>
          </Row>
        </Form>
      </div>
  );
};

export default CourseSearchBar;